import React, { useEffect, useState } from 'react';
import { Table, Button, Alert, Container, Card, Row, Col, Modal } from 'react-bootstrap';
import api from '../../services/api';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import moment from 'moment';

const LeaveAll = ({ token }) => {
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchLeaves(),
          fetchHolidays()
        ]);
      } catch (error) {
        setMessage('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchLeaves = async () => {
    const res = await api.get('/leaves/all');
    const sortedLeaves = [...res.data.leaves].sort((a, b) =>
      new Date(b.start_date) - new Date(a.start_date)
    );
    setLeaves(sortedLeaves);
  };

  const fetchHolidays = async () => {
    const today = moment().format('YYYY-MM-DD');
    const res = await api.get('/holidays');
    const upcomingHolidays = res.data.filter(holiday =>
      holiday.date >= today
    ).sort((a, b) => new Date(a.date) - new Date(b.date));
    setHolidays(upcomingHolidays);
  };

  const handleStatusChange = (id, status) => {
    api.put(`/leaves/${id}/status`, { status })
      .then(() => {
        setMessage(`Leave ${status}`);
        fetchLeaves();
      })
      .catch(() => setMessage('Failed to update leave status'));
  };

  if (loading) {
    return (
      <Layout>
        <TopBar title="Leave Management" />
        <Container className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <TopBar title="Leave Management" />
      <Container fluid className="mt-4">
        {message && <Alert variant="info">{message}</Alert>}

        <Row>
          <Col xl={8} lg={12} className="mb-4">
            <Card className="h-100">
              <Card.Header as="h5">Leave Requests</Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table bordered striped hover className="mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Employee</th>
                        <th>Type</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Days</th>
                        <th>Status</th>
                        <th>Reason</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaves.map((leave, index) => (
                        <tr key={index} className={leave.status === 'pending' ? 'table-warning' : ''}>
                          <td>{index + 1}</td>
                          <td>{leave.employee?.name || 'N/A'}</td>
                          <td>{leave.type?.name || 'N/A'}</td>
                          <td>{moment(leave.start_date).format('MMM D, YYYY')}</td>
                          <td>{moment(leave.end_date).format('MMM D, YYYY')}</td>
                          <td>{
                            (() => {
                              const days = moment(leave.end_date).diff(moment(leave.start_date), 'days') + 1;
                              const lf = (leave.leave_for || '').toString().toLowerCase();
                              const isHalf = lf.includes('half') || lf === 'h' || lf.includes('morning') || lf.includes('afternoon');
                              const val = isHalf ? days * 0.5 : days;
                              return Number.isInteger(val) ? val : val.toFixed(1);
                            })()
                          }</td>
                          <td>
                            <span className={`badge bg-${leave.status === 'approved' ? 'success' :
                              leave.status === 'rejected' ? 'danger' : 'warning'
                              }`}>
                              {leave.status}
                            </span>
                          </td>
                          <td>{leave.reason || '-'}</td>
                          <td>
                            <div className="d-flex gap-2">
                              {leave.status === 'pending' && (
                                <>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleStatusChange(leave.id, 'approved')}>
                                    Approve
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleStatusChange(leave.id, 'rejected')}>
                                    Reject
                                  </Button>
                                </>
                              )}
                              {leave.status === 'approved' && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => {
                                    setPendingAction({ id: leave.id, status: 'pending' });
                                    setShowConfirm(true);
                                  }}
                                  title="Revert to pending (restores leave days)">
                                  Revert
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4} lg={12}>
            <Card className="h-100">
              <Card.Header as="h5">Upcoming Holidays</Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Day</th>
                        <th>Holiday</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.map((holiday, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{moment(holiday.date).format('MMM D, YYYY')}</td>
                          <td>{moment(holiday.date).format('dddd')}</td>
                          <td>{holiday.description}</td>
                          <td>
                            <span className={`badge bg-${holiday.type === 'national' ? 'primary' : 'info'
                              }`}>
                              {holiday.type}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {holidays.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center">No upcoming holidays found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to revert this approved leave to pending? This will restore the leave days to the quota.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            No, Cancel
          </Button>
          <Button variant="warning" onClick={() => {
            handleStatusChange(pendingAction.id, pendingAction.status);
            setShowConfirm(false);
          }}>
            Yes, Revert to Pending
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default LeaveAll;
